/**
 * Shared MUI decision chrome — approve / correct layouts for KYC, Key, Exceptions.
 * Replaces legacy pg-decision-* CSS. One theme only.
 */
import type { ReactNode } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

export function DecisionLayout({
  detail,
  decision,
}: {
  detail: ReactNode;
  decision: ReactNode;
}) {
  return (
    <Grid container spacing={{ xs: 1.5, sm: 2, lg: 2.5 }} sx={{ alignItems: "flex-start" }}>
      {/* Stack through phone + large phone; split from tablet; roomier from laptop */}
      <Grid size={{ xs: 12, md: 7, lg: 8 }}>{detail}</Grid>
      <Grid size={{ xs: 12, md: 5, lg: 4 }}>
        <Box
          sx={{
            position: { md: "sticky" },
            top: { md: 16, lg: 24 },
          }}
        >
          {decision}
        </Box>
      </Grid>
    </Grid>
  );
}

export function DetailPanel({
  title,
  badge,
  children,
  "aria-label": ariaLabel,
}: {
  title: ReactNode;
  badge?: ReactNode;
  children: ReactNode;
  "aria-label"?: string;
}) {
  return (
    <Card variant="outlined" aria-label={ariaLabel} sx={{ mb: 0 }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h3" sx={{ m: 0, minWidth: 0, wordBreak: "break-word" }}>
            {title}
          </Typography>
          {badge}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

export function DecisionPanel({
  title = "Decision",
  hint,
  children,
  "aria-label": ariaLabel = "Decision",
}: {
  title?: string;
  hint?: ReactNode;
  children: ReactNode;
  "aria-label"?: string;
}) {
  return (
    <Card variant="outlined" aria-label={ariaLabel} sx={{ mb: 0 }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
        <Typography variant="h3" sx={{ mb: 1 }}>
          {title}
        </Typography>
        {hint ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {hint}
          </Typography>
        ) : null}
        <Stack spacing={1.5}>{children}</Stack>
      </CardContent>
    </Card>
  );
}

export function DetailRows({ rows }: { rows: { label: string; value: ReactNode }[] }) {
  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      {rows.map((r) => (
        <Stack
          key={r.label}
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.25, sm: 2 }}
          sx={{ justifyContent: "space-between", alignItems: { sm: "flex-start" } }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, minWidth: 88 }}>
            {r.label}
          </Typography>
          <Box sx={{ flex: 1, textAlign: { sm: "right" }, wordBreak: "break-word" }}>{r.value}</Box>
        </Stack>
      ))}
    </Stack>
  );
}

export function ReasonPicker<T extends string>({
  options,
  value,
  onToggle,
  labels,
  "aria-label": ariaLabel = "Reasons",
}: {
  options: readonly T[];
  value: T[];
  onToggle: (reason: T) => void;
  labels: Record<T, string>;
  "aria-label"?: string;
}) {
  return (
    <ToggleButtonGroup
      orientation="vertical"
      exclusive={false}
      fullWidth
      aria-label={ariaLabel}
      sx={{
        gap: 1,
        "& .MuiToggleButtonGroup-grouped": {
          border: 1,
          borderColor: "divider",
          borderRadius: "8px !important",
          textTransform: "none",
          justifyContent: "flex-start",
          px: 1.5,
          py: 1.25,
          minHeight: { xs: 48, sm: 44 },
        },
      }}
    >
      {options.map((r) => {
        const selected = value.includes(r);
        return (
          <ToggleButton
            key={r}
            value={r}
            selected={selected}
            onChange={() => onToggle(r)}
            color="warning"
            sx={{
              "&.Mui-selected": {
                bgcolor: "rgba(217, 119, 6, 0.12)",
                borderColor: "warning.main",
                color: "warning.dark",
                fontWeight: 600,
              },
            }}
          >
            {labels[r]}
          </ToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}

export function NotifyPreview({ title = "Notification preview", children }: { title?: string; children: ReactNode }) {
  return (
    <Alert severity="info" variant="outlined" sx={{ alignItems: "flex-start" }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Alert>
  );
}

export function PhotoGrid({ children }: { children: ReactNode }) {
  return (
    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
      {children}
    </Grid>
  );
}

export function PhotoThumb({
  label,
  href,
  failedFallback,
}: {
  label: string;
  href?: string | null;
  failedFallback?: ReactNode;
}) {
  if (href) {
    return (
      <Grid size={{ xs: 6 }}>
        <Box
          component="a"
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{
            display: "block",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            textDecoration: "none",
            color: "inherit",
            bgcolor: "background.default",
          }}
        >
          <Box
            component="img"
            src={href}
            alt={label}
            loading="lazy"
            sx={{ display: "block", width: "100%", height: 120, objectFit: "cover" }}
          />
          <Typography variant="caption" sx={{ display: "block", p: 1, fontWeight: 600 }}>
            {label}
          </Typography>
        </Box>
      </Grid>
    );
  }
  return (
    <Grid size={{ xs: 6 }}>
      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          height: 120,
          display: "grid",
          placeItems: "center",
          bgcolor: "background.default",
          color: "text.secondary",
          px: 1,
          textAlign: "center",
        }}
      >
        <Typography variant="caption">{failedFallback ?? label}</Typography>
      </Box>
    </Grid>
  );
}
